import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  listProducts,
  listVendors,
  listCategories,
  type Product,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Star,
  Pencil,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ProductFormModal } from "@/components/ProductFormModal";

const ITEMS_PER_PAGE = 20;

export default function Products() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [featuredFilter, setFeaturedFilter] = useState<string>("all");
  const [page, setPage] = useState(0);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch vendors and categories for filter dropdowns
  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: () => listVendors({ limit: 100 }),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories({ limit: 100 }),
  });

  // Build query params
  const queryParams = useMemo(() => {
    const params: Parameters<typeof listProducts>[0] = {
      limit: ITEMS_PER_PAGE,
      offset: page * ITEMS_PER_PAGE,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (vendorFilter !== "all") params.vendorId = vendorFilter;
    if (categoryFilter !== "all") params.categoryId = categoryFilter;
    if (activeFilter !== "all") params.active = activeFilter === "true";
    if (featuredFilter !== "all") params.featured = featuredFilter === "true";
    return params;
  }, [debouncedSearch, vendorFilter, categoryFilter, activeFilter, featuredFilter, page]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["products", queryParams],
    queryFn: () => listProducts(queryParams),
  });

  const totalPages = data ? Math.ceil(data.total / ITEMS_PER_PAGE) : 0;

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setVendorFilter("all");
    setCategoryFilter("all");
    setActiveFilter("all");
    setFeaturedFilter("all");
    setPage(0);
  };

  const hasActiveFilters =
    search || vendorFilter !== "all" || categoryFilter !== "all" ||
    activeFilter !== "all" || featuredFilter !== "all";

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return <div className="text-gray-500">Loading products...</div>;
  }

  if (error) {
    return <div className="text-red-500">Error loading products</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Products</h1>
        <Button onClick={handleAddProduct}>
          <Plus className="mr-2 h-4 w-4" />
          Add Product
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 rounded-lg bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Vendor Filter */}
          <Select value={vendorFilter} onValueChange={(v) => { setVendorFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendorsData?.items.map((vendor) => (
                <SelectItem key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Category Filter */}
          <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categoriesData?.items.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active Filter */}
          <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {/* Featured Filter */}
          <Select value={featuredFilter} onValueChange={(v) => { setFeaturedFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Featured" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              <SelectItem value="true">Featured</SelectItem>
              <SelectItem value="false">Not Featured</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          )}
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-500">
          {data ? (
            <>
              Showing {data.items.length} of {data.total} products
              {hasActiveFilters && " (filtered)"}
            </>
          ) : (
            "Loading..."
          )}
        </div>
      </div>

      <div className="rounded-lg bg-white shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-sm font-medium text-gray-500">
              <th className="px-6 py-3">Product</th>
              <th className="px-6 py-3">SKU</th>
              <th className="px-6 py-3">Vendor</th>
              <th className="px-6 py-3">Category</th>
              <th className="px-6 py-3">Price</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No products found. Create your first product to get started.
                </td>
              </tr>
            ) : (
              data?.items.map((product: Product) => (
                <tr key={product.id} className="border-b">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {product.thumbnailKey ? (
                        <img
                          src={`/api/uploads/${product.thumbnailKey}`}
                          alt={product.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 text-sm font-medium">
                          {product.name[0]}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{product.name}</span>
                          {product.featured && (
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          )}
                        </div>
                        {product.dimensions && (
                          <span className="text-xs text-gray-500">
                            {product.dimensions.width} x {product.dimensions.height} x{" "}
                            {product.dimensions.depth} {product.dimensions.unit}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono text-sm text-gray-500">
                    {product.sku}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {product.vendor?.name ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {product.category?.name ?? "-"}
                  </td>
                  <td className="px-6 py-4">
                    {product.price ? (
                      <span className="font-medium">
                        {product.currency} {product.price.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        product.active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {product.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditProduct(product)}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-white px-6 py-3 shadow-sm">
          <div className="text-sm text-gray-500">
            Page {page + 1} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ProductFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        product={editingProduct}
      />
    </div>
  );
}
